import { Fingerprint } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { authClient, useSession } from "@/lib/auth-client";
import {
  CODE_LENGTH,
  codeDigits,
  deviceName,
  friendlyAuthError,
  isEmail,
  nameFromEmail,
} from "@/lib/sign-in";

type Step = "email" | "code" | "name" | "passkey";

const RESEND_SECONDS = 30;

const inputClass =
  "w-full rounded-xl border border-input bg-background px-3 py-2 text-sm shadow-xs outline-none transition-colors focus:ring-2 focus:ring-brand/30 focus:border-brand/60";
const primaryClass =
  "w-full rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50";
const secondaryClass =
  "w-full flex items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50";

const passkeysSupported = () => typeof window !== "undefined" && "PublicKeyCredential" in window;

function ErrorNote({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div role="alert" className="rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive">
      {message}
    </div>
  );
}

/**
 * Passwordless sign-in / sign-up, as in maquette (AUTH.md → "Signing in"): email → 6-digit code →
 * (new accounts) name → optional passkey. Returning users with a passkey can skip straight in from
 * the email field.
 */
export function SignInForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteId = searchParams.get("invite");
  const next = inviteId ? `/invite/${inviteId}` : "/";
  const session = useSession();
  const [step, setStep] = useState<Step>("email");
  // An invite fills in the address it was sent to (maquette's `?email=`).
  const [email, setEmail] = useState(searchParams.get("email") ?? "");

  const finish = () => navigate(next, { replace: true });

  // Just signed out to switch accounts (the invite page): the session store may still hold the
  // old user for a moment, so don't take it as "already signed in".
  const signedOut = (useLocation().state as { signedOut?: boolean } | null)?.signedOut === true;

  // Already signed in (an old link, another tab): go on to where sign-in would have led.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only react to the initial session load
  useEffect(() => {
    if (!signedOut && step === "email" && session.data?.user) finish();
  }, [session.data?.user]);

  // The sign-in response carries the user: no second request to learn whether they have a name.
  const afterCode = async (user: { name?: string | null }) => {
    if (!user.name) setStep("name");
    else finish();
  };

  switch (step) {
    case "email":
      return (
        <EmailStep
          initial={email}
          onSent={(value) => {
            setEmail(value);
            setStep("code");
          }}
          onPasskey={finish}
        />
      );
    case "code":
      return <CodeStep email={email} onBack={() => setStep("email")} onVerified={afterCode} />;
    case "name":
      return (
        <NameStep
          email={email}
          onDone={() => (passkeysSupported() ? setStep("passkey") : finish())}
        />
      );
    case "passkey":
      return <PasskeyStep onDone={finish} />;
  }
}

function EmailStep({
  initial,
  onSent,
  onPasskey,
}: {
  initial: string;
  onSent: (email: string) => void;
  onPasskey: () => void;
}) {
  const [email, setEmail] = useState(initial);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState<"code" | "passkey" | null>(null);

  // Offer saved passkeys right in the email field's autofill (conditional UI), where supported.
  // biome-ignore lint/correctness/useExhaustiveDependencies: start once per mount
  useEffect(() => {
    let active = true;
    void (async () => {
      if (!(await window.PublicKeyCredential?.isConditionalMediationAvailable?.())) return;
      const res = await authClient.signIn.passkey({ autoFill: true });
      if (active && res && !res.error) onPasskey();
    })().catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!isEmail(value)) return setError("Enter a valid email address.");
    setBusy("code");
    setError(undefined);
    const res = await authClient.emailOtp.sendVerificationOtp({ email: value, type: "sign-in" });
    setBusy(null);
    if (res.error) return setError(friendlyAuthError(res.error));
    onSent(value);
  };

  const passkey = async () => {
    setBusy("passkey");
    setError(undefined);
    const res = await authClient.signIn.passkey();
    setBusy(null);
    if (res?.error)
      return setError(
        "code" in res.error && res.error.code === "ACCOUNT_SUSPENDED"
          ? friendlyAuthError(res.error)
          : "That passkey didn't work. Use an email code instead.",
      );
    onPasskey();
  };

  return (
    <div className="space-y-4">
      <ErrorNote message={error} />
      <form onSubmit={submit} className="space-y-4" noValidate>
        <div className="space-y-2">
          <label htmlFor="signin-email" className="text-sm font-medium">
            Email
          </label>
          <input
            id="signin-email"
            type="email"
            autoComplete="email webauthn"
            // biome-ignore lint/a11y/noAutofocus: the only field on the page
            autoFocus
            placeholder="you@example.com"
            className={inputClass}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <button type="submit" disabled={busy !== null} className={primaryClass}>
          {busy === "code" ? "Sending a code..." : "Continue with email"}
        </button>
      </form>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">or</span>
        </div>
      </div>

      <button type="button" onClick={passkey} disabled={busy !== null} className={secondaryClass}>
        <Fingerprint className="size-4" />
        {busy === "passkey" ? "Checking passkey..." : "Sign in with a passkey"}
      </button>

      <p className="text-center text-xs text-muted-foreground">
        We'll email you a six-digit code. New to Kourai? The same code creates your account.
      </p>
    </div>
  );
}

function CodeStep({
  email,
  onBack,
  onVerified,
}: {
  email: string;
  onBack: () => void;
  onVerified: (user: { name?: string | null }) => Promise<void>;
}) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(RESEND_SECONDS);

  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const verify = async (otp: string) => {
    setBusy(true);
    setError(undefined);
    const res = await authClient.signIn.emailOtp({ email, otp });
    if (res.error) {
      setBusy(false);
      setCode("");
      return setError(friendlyAuthError(res.error));
    }
    await onVerified(res.data.user);
  };

  const resend = async () => {
    setWait(RESEND_SECONDS);
    setError(undefined);
    setCode("");
    const res = await authClient.emailOtp.sendVerificationOtp({ email, type: "sign-in" });
    if (res.error) setError(friendlyAuthError(res.error));
  };

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-muted-foreground hover:text-primary transition-colors"
      >
        ← Use a different email
      </button>
      <p className="text-sm text-muted-foreground">
        Enter the code we sent to <span className="font-medium text-foreground">{email}</span>.
      </p>
      <ErrorNote message={error} />
      <input
        aria-label="Six-digit code"
        inputMode="numeric"
        autoComplete="one-time-code"
        // biome-ignore lint/a11y/noAutofocus: the only field on the page
        autoFocus
        disabled={busy}
        maxLength={CODE_LENGTH}
        placeholder="000000"
        className={`${inputClass} text-center text-2xl font-semibold tracking-[0.5em] tabular-nums`}
        value={code}
        onChange={(e) => {
          const digits = codeDigits(e.target.value);
          setCode(digits);
          if (digits.length === CODE_LENGTH) void verify(digits);
        }}
      />
      {busy && <p className="text-xs text-muted-foreground">Checking…</p>}
      <p className="text-xs text-muted-foreground">
        Didn't get it? Check spam, or{" "}
        {wait > 0 ? (
          <span className="tabular-nums">resend in 0:{String(wait).padStart(2, "0")}</span>
        ) : (
          <button type="button" onClick={resend} className="font-medium text-primary underline">
            send a new code
          </button>
        )}
        .
      </p>
    </div>
  );
}

function NameStep({ email, onDone }: { email: string; onDone: () => void }) {
  const [name, setName] = useState(() => nameFromEmail(email));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setError("Add a name so your workspace knows who's who.");
    setBusy(true);
    const res = await authClient.updateUser({ name: name.trim() });
    setBusy(false);
    if (res.error) return setError(friendlyAuthError(res.error));
    onDone();
  };

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      <p className="text-sm text-muted-foreground">Nice to meet you. What should we call you?</p>
      <ErrorNote message={error} />
      <div className="space-y-2">
        <label htmlFor="signin-name" className="text-sm font-medium">
          Your name
        </label>
        <input
          id="signin-name"
          autoComplete="name"
          // biome-ignore lint/a11y/noAutofocus: the only field on the page
          autoFocus
          className={inputClass}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <button type="submit" disabled={busy} className={primaryClass}>
        {busy ? "Saving..." : "Continue"}
      </button>
    </form>
  );
}

function PasskeyStep({ onDone }: { onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const add = async () => {
    setBusy(true);
    setError(undefined);
    const res = await authClient.passkey.addPasskey({ name: deviceName(navigator.userAgent) });
    setBusy(false);
    if (res?.error) return setError("Couldn't add a passkey. You can do it later in Settings.");
    onDone();
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Faster next time: add a passkey to sign in with Touch ID, Face ID or your device's screen
        lock. Nothing to type, nothing to remember.
      </p>
      <ErrorNote message={error} />
      <button type="button" onClick={add} disabled={busy} className={secondaryClass}>
        <Fingerprint className="size-4" />
        {busy ? "Adding..." : "Add a passkey"}
      </button>
      <button
        type="button"
        onClick={onDone}
        disabled={busy}
        className="w-full rounded-xl px-4 py-2 text-sm text-muted-foreground hover:bg-muted transition-colors"
      >
        Not now
      </button>
    </div>
  );
}
